import React, { Suspense, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
    graphql,
    PreloadedQuery,
    useMutation,
    usePreloadedQuery,
    useQueryLoader,
} from "react-relay";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import PropTypes from "prop-types";

import { ProfileIconButtonGateQuery as ProfileIconButtonGateQueryType } from "__generated__/ProfileIconButtonGateQuery.graphql";
import { ProfileIconButtonLogoutMutation as ProfileIconButtonLogoutMutationType } from "__generated__/ProfileIconButtonLogoutMutation.graphql";
import { ProfileIconButtonProfileQuery as ProfileIconButtonProfileQueryType } from "__generated__/ProfileIconButtonProfileQuery.graphql";

import ErrorContainer from "apps/components/ErrorContainer";
import LetterAvatar from "common/DS/Avatar";
import { TranslationFn } from "lib/types";

const MOUNT_ELEMENT_ID = "nav-user-menu";

const ProfileIconButtonGateQuery = graphql`
    query ProfileIconButtonGateQuery {
        me {
            id
            isHubAuth
        }
    }
`;

const ProfileIconButtonProfileQuery = graphql`
    query ProfileIconButtonProfileQuery {
        me {
            id
            fullName
            hubLink
        }
    }
`;

const ProfileIconButtonLogoutMutation = graphql`
    mutation ProfileIconButtonLogoutMutation {
        logout
    }
`;

type MenuProps = {
    queryRef: PreloadedQuery<ProfileIconButtonProfileQueryType>;
};

const isOnHub = (hubLink: string) =>
    window.location.pathname.replace(/\/+$/, "") === hubLink.replace(/\/+$/, "");

const ProfileMenu = ({ queryRef }: MenuProps, { t: _ }: { t: TranslationFn }) => {
    const { me } = usePreloadedQuery(ProfileIconButtonProfileQuery, queryRef);
    const [logout] = useMutation<ProfileIconButtonLogoutMutationType>(
        ProfileIconButtonLogoutMutation,
    );
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const open = Boolean(anchorEl);
    const close = () => setAnchorEl(null);

    const handleLogout = () => {
        close();
        logout({
            variables: {},
            updater: (store) => {
                const meRecord = store.getRoot().getLinkedRecord("me");
                if (meRecord) {
                    meRecord.setValue(false, "isHubAuth");
                    meRecord.invalidateRecord();
                }
            },
            onCompleted: () => {
                window.location.href = "/hub";
            },
        });
    };

    return (
        <>
            <IconButton
                aria-label={_("Profile menu")}
                aria-haspopup="true"
                aria-expanded={open}
                onClick={(e) => setAnchorEl(e.currentTarget)}
                size="small"
            >
                <LetterAvatar fullName={me.fullName} />
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={close}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
                {!isOnHub(me.hubLink) && (
                    <MenuItem
                        className="nav-user-menu-item"
                        component="a"
                        href={me.hubLink}
                        onClick={close}
                    >
                        {_("Back to Hub")}
                    </MenuItem>
                )}
                <MenuItem
                    className="nav-user-menu-item"
                    component="a"
                    href="/hub/contact-info"
                    onClick={close}
                >
                    {_("Edit Contact Info")}
                </MenuItem>
                <MenuItem className="nav-user-menu-item" onClick={handleLogout}>
                    {_("Logout")}
                </MenuItem>
            </Menu>
        </>
    );
};

ProfileMenu.contextTypes = {
    t: PropTypes.func,
};

const ProfileMenuLoader = () => {
    const [queryRef, loadQuery, disposeQuery] = useQueryLoader<ProfileIconButtonProfileQueryType>(
        ProfileIconButtonProfileQuery,
    );

    useEffect(() => {
        loadQuery({});
        return () => {
            disposeQuery();
        };
    }, [loadQuery, disposeQuery]);

    if (!queryRef) return null;

    return <ProfileMenu queryRef={queryRef} />;
};

type GateProps = {
    queryRef: PreloadedQuery<ProfileIconButtonGateQueryType>;
};

const HubAuthGate = ({ queryRef }: GateProps) => {
    const { me } = usePreloadedQuery(ProfileIconButtonGateQuery, queryRef);
    if (!me.isHubAuth) return null;
    return (
        <Suspense fallback={null}>
            <ProfileMenuLoader />
        </Suspense>
    );
};

const HubAuthLoader = () => {
    const [queryRef, loadQuery, disposeQuery] = useQueryLoader<ProfileIconButtonGateQueryType>(
        ProfileIconButtonGateQuery,
    );

    useEffect(() => {
        loadQuery({});
        return () => {
            disposeQuery();
        };
    }, [loadQuery, disposeQuery]);

    if (!queryRef) return null;

    return <HubAuthGate queryRef={queryRef} />;
};

const ProfileIconButton = () => {
    const mountEl =
        typeof document !== "undefined" ? document.getElementById(MOUNT_ELEMENT_ID) : null;
    if (!mountEl) return null;
    return createPortal(
        <ErrorContainer hideContainer fallback={null}>
            <Suspense fallback={null}>
                <HubAuthLoader />
            </Suspense>
        </ErrorContainer>,
        mountEl,
    );
};

export default ProfileIconButton;
